import React, { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { FormInputs } from "../../../../../../types/walletTypes";
import { savedAsiaPayoutData } from "../../../../redux/formDataSlice";
import { merchantBusinessKeys } from "../../../../../Settings/settingsSlice";
import { KlashaCNYQuotation } from "../../../../redux/slice";
import { encryptPayloadWithMerchantKeyJIT } from "../../../../../../utils/encryptionAlgorithm";
import { formatCurrencyAmount } from "../../../../../../utils/currency";
import { debounce } from "lodash";
import {
  ContentWrapper,
  InputWrapper,
  Error,
  RateBox,
  BalanceExceptionWrapper,
} from "../../../shared/styles";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import InputAndSelect from "../../../../../../components/common/InputAndSelect";
import { Button } from "../../../../../../components/common/Button";
import Loader from "../../../../../../components/common/Loader";
import { ReactComponent as NotifyIcon } from "../../../../../../assets/icons/notify-icon.svg";
import { CountryConfig } from "../../../shared/types";
import { LIST_OF_UAE_AED_PAYOUT_CURRENCIES } from "../../../../const";
import { PURPOSE_DESCRIPTION_OPTIONS } from "../../../shared/const";
import { usePurposeCodeLookup } from "../../../shared/hooks/usePurposeCodeLookup";

type WalletProps = {
  currency: string;
  availableBalance: number;
};

interface UAEAmountFormProps {
  config?: CountryConfig;
  handleNext?: () => void;
  handlePrevious?: () => void;
}

const UAEAmountForm: React.FC<UAEAmountFormProps> = ({ handleNext }) => {
  const dispatch = useAppDispatch();
  const asiaPayoutData: FormInputs = useAppSelector(
    (state) => (state.formData as any)?.asiaPayoutData?.aed || {}
  );
  const { keys } = useAppSelector((state) => state.settings);
  const { wallets, loading } = useAppSelector((state) => state.wallets || {});
  const { businessId } = useAppSelector((state) => state.users);

  const { getPurposeCodeByDescription } = usePurposeCodeLookup();

  const [userData, setUserData] = useState<FormInputs>(() => ({
    sourceCurrency: asiaPayoutData?.sourceCurrency || "USD",
    paymentMethod: "bank_transfer",
    ...asiaPayoutData,
  }));

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    defaultValues: userData,
  });

  useEffect(() => {
    if (asiaPayoutData && Object.keys(asiaPayoutData).length > 0) {
      setUserData((prev) => ({
        sourceCurrency: "USD",
        paymentMethod: "bank_transfer",
        ...prev,
        ...asiaPayoutData,
      }));
      reset({ sourceCurrency: "USD", paymentMethod: "bank_transfer", ...asiaPayoutData });
    }
  }, [asiaPayoutData, reset]);

  const sourceCurrency = userData?.sourceCurrency || "USD";
  const filteredWallets = (wallets || []) as WalletProps[];
  const CURRENT_WALLET = filteredWallets.find((w) => w.currency === sourceCurrency);

  const handleDataUpdate = (field: keyof FormInputs, value: any) => {
    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setValue(field, value);
  };

  const handleAmountChange = (enteredAmount: any) => {
    const numAmount =
      typeof enteredAmount === "number" ? enteredAmount : parseFloat(enteredAmount) || 0;
    setUserData((prev) => ({
      ...prev,
      amount: numAmount,
    }));
    setValue("amount", numAmount);
  };

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    if (!CURRENT_WALLET) return;
    const availableBalance = parseFloat(
      Number(CURRENT_WALLET.availableBalance).toFixed(2)
    );
    const totalAmount = parseFloat((userData?.totalAmount ?? 0).toFixed(2));
    if (availableBalance < totalAmount) {
      handleDataUpdate("balanceError", true);
    } else {
      handleDataUpdate("balanceError", false);
    }
  }, [CURRENT_WALLET, userData?.totalAmount]);

  const debouncedGetKlashaQuotation = useCallback(
    debounce(async (currentData, currentBusinessId) => {
      const activeSourceCurrency = currentData?.sourceCurrency || "USD";
      if (!currentData?.amount || !currentBusinessId) {
        return;
      }

      const data: Record<string, any> = {
        destinationAmount: currentData.amount,
        sourceCurrency: activeSourceCurrency,
        destinationCurrency: "AED",
        transferType: "B2B",
        service: "BANK_ACCOUNT",
        fundSource: "CASH",
      };

      try {
        const { encryptedMessage, publicKey } = await encryptPayloadWithMerchantKeyJIT(
          currentBusinessId,
          data
        );
        const payload = { message: encryptedMessage };
        const token = publicKey;

        const action = await dispatch(
          KlashaCNYQuotation({ message: payload, token })
        );

        if (KlashaCNYQuotation.fulfilled.match(action)) {
          const { fxRate, fee, sourceAmount, expiration, id, reference } = action.payload;
          const convertedValue = Number(currentData.amount / fxRate);

          setUserData((prev) => ({
            ...prev,
            sourceCurrency: activeSourceCurrency,
            rate: fxRate,
            convertedAmount: convertedValue,
            totalAmount: Number(sourceAmount + fee),
            fee,
            expiration,
            quotationId: id,
            reference,
            sourceAmount,
            rateError: false,
          }));
        } else {
          setUserData((prev) => ({
            ...prev,
            rate: null,
            fee: null,
            totalAmount: 0,
            rateError: true,
          }));
        }
      } catch (err) {
        setUserData((prev) => ({
          ...prev,
          rateError: true,
        }));
      }
    }, 600),
    [dispatch]
  );

  useEffect(() => {
    if (userData?.amount && businessId) {
      debouncedGetKlashaQuotation(userData, businessId);
    }
  }, [userData?.amount, userData?.sourceCurrency, businessId, debouncedGetKlashaQuotation]);

  const onSubmit = (data: FormInputs) => {
    const selectedDesc = data.description || userData?.description;
    const purposeCode = getPurposeCodeByDescription(selectedDesc);

    const updatedData: FormInputs = {
      ...asiaPayoutData,
      ...data,
      ...userData,
      description: selectedDesc,
      purpose: purposeCode,
      purposeCode,
      paymentMethod: "bank_transfer",
      destinationCurrency: "AED",
    };
    dispatch(savedAsiaPayoutData({ country: "aed", data: updatedData }));
    handleNext?.();
  };

  const disableButton =
    loading || !isValid || !userData?.rate || userData?.balanceError || !userData?.description;

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      <InputWrapper>
        <CustomSelect
          $width="100%"
          placeholder="Beneficiary country"
          options={[{ label: "United Arab Emirates", value: "AED" }]}
          value="AED"
          lightBorder
          disabled
        />
      </InputWrapper>

      <InputWrapper>
        <CustomSelect
          $width="100%"
          placeholder="Payment method"
          options={[{ label: "Bank Transfer", value: "bank_transfer" }]}
          value="bank_transfer"
          lightBorder
          disabled
        />
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="amount"
          control={control}
          rules={{
            required: "Amount is required",
            min: { value: 1, message: "Amount must be greater than 0" },
          }}
          render={({ field }) => (
            <InputAndSelect
              width="100%"
              placeholder="Amount"
              options={[{ label: "AED", value: "AED" }]}
              selectValue="AED"
              onSelectChange={() => {}}
              amountValue={field.value || ""}
              onAmountChange={(val) => {
                field.onChange(val);
                handleAmountChange(val);
              }}
            />
          )}
        />
        {errors.amount?.message && <Error>{String(errors.amount.message)}</Error>}
      </InputWrapper>

      {userData?.rate != null && (
        <RateBox>
          <span className="fee-box">
            x <span>{formatCurrencyAmount("", userData?.rate ?? 0, 4)}</span>
          </span>
          <span>Exchange rate</span>
        </RateBox>
      )}

      {userData?.fee != null && (
        <RateBox>
          <span className="fee-box">
            + <span>{formatCurrencyAmount("", userData?.fee ?? 0, 2)}</span>
          </span>
          <span>Processing fee</span>
        </RateBox>
      )}

      {userData?.rateError && (
        <Error>Exchange rate unavailable. Please try again or contact support.</Error>
      )}

      {userData?.totalAmount > 0 && (
        <div>
          <Controller
            name="sourceCurrency"
            control={control}
            render={({ field }) => (
              <InputAndSelect
                amountValue={userData?.totalAmount?.toFixed(2) ?? ""}
                width="100%"
                placeholder="Total you will be charged"
                options={LIST_OF_UAE_AED_PAYOUT_CURRENCIES}
                selectValue={field.value || sourceCurrency}
                onSelectChange={(curr) => {
                  field.onChange(curr);
                  handleDataUpdate("sourceCurrency", curr);
                }}
              />
            )}
          />
          {userData?.balanceError && (
            <BalanceExceptionWrapper>
              <NotifyIcon /> <span>Exceeds wallet balance.</span>
              <Link to={`/wallets/${sourceCurrency.toLowerCase()}`}> Fund wallet</Link>
            </BalanceExceptionWrapper>
          )}
        </div>
      )}

      <InputWrapper>
        <Controller
          name="description"
          control={control}
          rules={{ required: "Description is required" }}
          render={({ field }) => (
            <CustomSelect
              $width="100%"
              value={field.value || userData?.description || undefined}
              onChange={(val) => {
                field.onChange(val);
                handleDataUpdate("description", val);
                const matchedCode = getPurposeCodeByDescription(val);
                handleDataUpdate("purposeCode", matchedCode);
              }}
              options={PURPOSE_DESCRIPTION_OPTIONS}
              placeholder="Description"
              lightBorder
            />
          )}
        />
        {errors.description?.message && (
          <Error>{String(errors.description.message)}</Error>
        )}
      </InputWrapper>

      <Button
        onClick={handleSubmit(onSubmit)}
        disabled={disableButton}
        label="Continue"
        height="48px"
      />
    </ContentWrapper>
  );
};

export default UAEAmountForm;
